// The organization's projects being deleted (APS-401): each is gone from every list while its
// deletion is open, so this is where its members see when it runs and its owners and admins
// cancel it (Settings › General, the shell's note on the project's own pages, and the
// new-project page of an organization left without projects). Canceling needs no confirmation:
// nothing is lost by it.
import { useQuery } from '@tanstack/react-query'
import { projectErasuresQuery, useCancelProjectErasure } from '../api/queries'
import type { ProjectErasure } from '../api/types'
import { useI18n } from '../i18n'
import { day } from '../lib/format'
import { ActionError } from './ActionError'

/** The open deletions, for every member; none answered (or no organization yet), no section. */
export function useProjectDeletions(orgId: string, enabled = true) {
  return useQuery({ ...projectErasuresQuery(orgId), enabled, throwOnError: false })
}

/**
 * The name of a project the organization's lists leave out because it is being deleted (the
 * bar's scope, the settings sidebar): asked only once the lists have answered without it.
 */
export function useHiddenProjectName(
  orgId: string,
  projectId: string,
  listed: boolean,
): string | undefined {
  const deletions = useProjectDeletions(orgId, !listed)
  return listed
    ? undefined
    : deletions.data?.erasures.find((e) => e.project_id === projectId)?.project_name
}

export function ProjectDeletions({
  orgId,
  erasures,
  canCancel,
  buttonClass = 'btn ghost sm',
  onCanceled,
}: {
  orgId: string
  erasures: ProjectErasure[]
  /** Owners and admins; members read when it runs, and who can cancel it. */
  canCancel: boolean
  /** The shell's note has Cancel as its one action: a plain `btn` there. */
  buttonClass?: string
  /** Where to go once canceled (the toast follows, flash.ts). */
  onCanceled?: (erasure: ProjectErasure) => unknown
}) {
  const i18n = useI18n()
  const { t } = i18n
  const cancel = useCancelProjectErasure(orgId)
  return (
    <>
      <ul className="se-deleting">
        {erasures.map((e) => (
          <li key={e.id}>
            <span>
              <b translate="no">{e.project_name}</b>
              <span>
                {e.status === 'scheduled'
                  ? t('settings.general.deleting.on', { date: day(i18n, e.scheduled_until) })
                  : t('settings.general.deleting.now')}
                {e.by_support ? ` · ${t('settings.general.deleting.support')}` : ''}
              </span>
            </span>
            {e.status !== 'scheduled' || e.by_support ? null : canCancel ? (
              <button
                type="button"
                className={buttonClass}
                disabled={cancel.isPending}
                aria-label={t('settings.general.deleting.cancelLabel', { project: e.project_name })}
                onClick={() =>
                  cancel.mutate({
                    projectId: e.project_id,
                    done: t('settings.general.deleting.back', { project: e.project_name }),
                    ...(onCanceled ? { afterCancel: () => onCanceled(e) } : {}),
                  })
                }
              >
                {t('settings.general.deleting.cancel')}
              </button>
            ) : (
              <span className="se-deleting-who">{t('settings.general.deleting.ownerCancels')}</span>
            )}
          </li>
        ))}
      </ul>
      {cancel.error ? <ActionError error={cancel.error} /> : null}
    </>
  )
}
